/**
 * AI-sofőr: ugyanazt a fizikát kapja, mint az ember – kizárólag InputState-et ad vissza,
 * a Kart-ot soha nem módosítja. Minden véletlen az Rng-ből jön.
 */
import type { CharacterDef, InputState, Vec2 } from '../core/types';
import { IDLE_INPUT } from '../core/types';
import type { Kart } from './kart';
import type { Track } from './track';
import { angleDiff, clamp } from '../core/math';
import { Rng } from '../core/rng';

export interface AiDanger {
  pos: Vec2;
  radius: number;
}

export interface AiBox {
  pos: Vec2;
  active: boolean;
}

export interface AiView {
  track: Track;
  karts: Kart[];
  time: number;
  hazards: AiDanger[];
  projectiles: AiDanger[];
  boxes: AiBox[];
}

export interface AiBrain {
  update(dt: number, kart: Kart, view: AiView): InputState;
}

/* ---------------------------------------------------------------- *
 * Hangolási állandók (a jelentésben is ezeket sorolom fel)
 * ---------------------------------------------------------------- */

/** Görbület-küszöbök 1/px-ben: ez alatt egyenes, ez felett szűk kanyar. */
const STRAIGHT_CURV = 0.0011;
const TIGHT_CURV = 0.0062;
/** Kanyar-előrejelzés: ennyi px-en belül nézzük a görbületet. */
const CORNER_LOOK = 190;
/** Veszély- és forgalom-előrejelzés px-ben. */
const DANGER_AHEAD = 165;
const KART_AHEAD = 135;
const BOX_AHEAD = 240;
/** Arányos kormányerősítés. */
const STEER_GAIN = 2.2;
/** Reakcióidő-tartomány másodpercben (skill 0 -> lassú, skill 1 -> gyors). */
const REACTION_SLOW = 0.3;
const REACTION_FAST = 0.08;
/** E görbület alatt tekintjük "elég egyenesnek" egy item használatához. */
const ITEM_STRAIGHT_CURV = 0.0032;

/** Csúcs-görbület (abszolút) egy sávban. */
const peakCurvature = (track: Track, s: number, from: number, to: number, step: number): number => {
  let peak = 0;
  for (let d = from; d <= to; d += step) {
    const c = Math.abs(track.curvatureAt(track.progressOfS(s + d)));
    if (c > peak) peak = c;
  }
  return peak;
};

/** Előjeles, simított görbület az ívvonalhoz (két minta átlaga). */
const signedCurvature = (track: Track, s: number, distance: number): number => {
  const a = track.curvatureAt(track.progressOfS(s + distance * 0.55));
  const b = track.curvatureAt(track.progressOfS(s + distance * 1.1));
  return a * 0.6 + b * 0.4;
};

export function createAi(character: CharacterDef, seed: number, skill: number): AiBrain {
  const rng = new Rng(seed);
  const s01 = clamp(skill, 0, 1);

  // --- személyiség: verseny közben már nem sorsolunk, minden stabil ---
  const reaction = REACTION_SLOW + (REACTION_FAST - REACTION_SLOW) * s01; // 0.30 .. 0.08 s
  const gripFactor = clamp(0.97 + (character.stats.grip - 7) * 0.012, 0.93, 1.03);
  const safety = clamp((0.70 + 0.2 * s01) * gripFactor, 0.6, 0.95); // az elméleti ívsebesség hányada
  const linePrecision = 0.72 + 0.28 * s01; // belső ív kihasználása
  const wobbleAmp = (1 - s01) * 0.2; // vonal-pontatlanság (roadHalfWidth arányában)
  const wobblePhase = rng.range(0, Math.PI * 2);
  const wobbleFreq = rng.range(0.35, 0.75);
  const laneBias = rng.range(-0.12, 0.12);
  const driftCurv = 0.0044 - 0.0014 * s01;
  const driftEndCurv = 0.0017;
  const itemDelay = clamp(rng.range(0.2, 1.2) * (1.2 - 0.35 * s01), 0.2, 1.2);
  const rngItem = rng.fork();

  // --- futó állapot ---
  let clock = 0;
  let steerHistory: { t: number; v: number }[] = [];
  let laneSmooth = 0;
  let driftHold = false;
  let driftHoldTime = 0;
  let driftLock = 0;
  let stuckTimer = 0;
  let reverseTimer = 0;
  let abilityLatch = false;
  let itemKnown: string | null = null;
  let itemFired = false;
  let itemWait = 0;
  let itemHold = 0;
  let lastShots: Vec2[] = [];

  const update = (dt: number, kart: Kart, view: AiView): InputState => {
    const out: InputState = { ...IDLE_INPUT };
    const track = view.track;
    clock += dt;

    // --- pálya-információ a kart helyén ---
    const info = track.surfaceAt(kart.pos);
    const halfW = track.def.roadHalfWidth;
    const maxLane = halfW * 0.9;
    const maxSpeed = Math.max(24, kart.maxSpeedNow);
    const forwardSpeed = Math.max(0, kart.speed);
    const lookahead = clamp(60 + forwardSpeed * 0.32, 60, 200);

    // --- kanyar-előrejelzés ---
    const nearPeak = peakCurvature(track, info.s, 15, 150, 35);
    const cornerPeak = peakCurvature(track, info.s, 20, CORNER_LOOK, 30);
    const cornerIntensity = clamp((cornerPeak - STRAIGHT_CURV) / (TIGHT_CURV - STRAIGHT_CURV), 0, 1);
    const straightness = clamp(1 - nearPeak / ITEM_STRAIGHT_CURV, 0, 1);
    const signedCurv = signedCurvature(track, info.s, lookahead * 0.85);

    // --- beragadás: 1.5 mp-ig < 25 px/s -> 0.8 mp hátramenet ---
    if (reverseTimer > 0) {
      reverseTimer = Math.max(0, reverseTimer - dt);
    } else {
      const planar = Math.hypot(kart.vel.x, kart.vel.y);
      stuckTimer = planar < 25 ? stuckTimer + dt : Math.max(0, stuckTimer - dt * 2);
      if (stuckTimer >= 1.5) {
        reverseTimer = 0.8;
        stuckTimer = 0;
        driftHold = false;
        driftHoldTime = 0;
        steerHistory = [];
      }
    }

    // --- képesség: egyenesben, beragadva, vagy hop-pal a járdán ---
    if (kart.abilityCooldown > 0) abilityLatch = false;
    if (!abilityLatch && kart.abilityCooldown <= 0) {
      // hop csak akkor, ha a járdán lassú/beragadt – egyébként egyenesben, löketként
      const hopRecover = character.ability.id === 'hop' && kart.surface === 'sidewalk' && forwardSpeed < maxSpeed * 0.45;
      if (
        reverseTimer > 0 ||
        stuckTimer > 0.8 ||
        hopRecover ||
        (straightness > 0.5 && forwardSpeed > maxSpeed * 0.5)
      ) {
        out.ability = true;
        abilityLatch = true;
      }
    }

    // --- kipördülés: kivárunk ---
    if (kart.spinTimer > 0) {
      steerHistory = [];
      driftHold = false;
      out.throttle = 0.4;
      return out;
    }

    // --- tolatás: ellenkormányozva, a középvonal felé ---
    if (reverseTimer > 0) {
      const center = track.posAt(track.progressOfS(info.s), 0);
      const toCenter = Math.atan2(center.y - kart.pos.y, center.x - kart.pos.x);
      const err = angleDiff(toCenter, kart.heading);
      out.steer = Math.abs(err) > 0.2 ? -Math.sign(err) * 0.35 : 0;
      out.brake = 1;
      out.throttle = 0;
      out.drift = false;
      return out;
    }

    /* ---------------- kitérés + mezőny-információ ---------------- */

    let avoidLane: number | null = null;
    let avoidWeight = 0;
    let hardBrake = 0;

    /** Egy akadály megkerülése: a több helyet adó oldalra húzódunk, ha lehet. */
    const pushAvoid = (lat: number, ahead: number, need: number, weight: number, dist: number): void => {
      if (ahead > DANGER_AHEAD) return;
      const roomR = maxLane - lat;
      const roomL = lat + maxLane;
      const rightOk = roomR >= need;
      const leftOk = roomL >= need;
      const preferRight = Math.abs(lat) < 4 ? roomR >= roomL : lat < 0;
      let target: number;
      if (preferRight && rightOk) target = lat + need;
      else if (!preferRight && leftOk) target = lat - need;
      else if (rightOk) target = lat + need;
      else if (leftOk) target = lat - need;
      else {
        // nincs elég hely: a fal felé húzódunk, de fékezünk
        target = roomR > roomL ? maxLane : -maxLane;
        hardBrake = Math.max(hardBrake, clamp(1 - dist / DANGER_AHEAD, 0.3, 1));
      }
      target = clamp(target, -maxLane, maxLane);
      const w = clamp(1 - ahead / DANGER_AHEAD, 0.25, 1) * weight;
      if (w > avoidWeight) {
        avoidWeight = w;
        avoidLane = target;
      }
    };

    // olajfolt / ragacs
    for (const hazard of view.hazards) {
      const dx = hazard.pos.x - kart.pos.x;
      const dy = hazard.pos.y - kart.pos.y;
      const dist = Math.hypot(dx, dy);
      if (dist > DANGER_AHEAD + 80) continue;
      const hit = track.nearest(hazard.pos);
      pushAvoid(hit.lateral, track.forward(info.s, hit.s), hazard.radius + kart.radius + 16, 1, dist);
    }

    // rakéták: a távolodókat (pl. a sajátunkat) nem kell kerülgetni
    const shots = view.projectiles;
    const shotVels: { pos: Vec2; vx: number; vy: number; radius: number }[] = [];
    for (const shot of shots) {
      let vx = 0;
      let vy = 0;
      let best = 40;
      for (const prev of lastShots) {
        const d = Math.hypot(shot.pos.x - prev.x, shot.pos.y - prev.y);
        if (d < best) {
          best = d;
          vx = shot.pos.x - prev.x;
          vy = shot.pos.y - prev.y;
        }
      }
      shotVels.push({ pos: shot.pos, vx, vy, radius: shot.radius });
    }
    lastShots = shots.map((shot) => ({ x: shot.pos.x, y: shot.pos.y }));
    for (const shot of shotVels) {
      const dx = shot.pos.x - kart.pos.x;
      const dy = shot.pos.y - kart.pos.y;
      const dist = Math.hypot(dx, dy);
      if (dist > DANGER_AHEAD + 80) continue;
      if (shot.vx * dx + shot.vy * dy > 0) continue;
      const hit = track.nearest(shot.pos);
      pushAvoid(hit.lateral, track.forward(info.s, hit.s), shot.radius + kart.radius + 18, 1.15, dist);
    }

    // mezőny: helyezés, előttünk/mögöttünk lévő kartok, forgalom-elkerülés
    let aheadKartDist = Infinity;
    let behindKartDist = Infinity;
    let rank = 1;
    for (const other of view.karts) {
      // a világ a célba érőket a pályán kívülre teszi: őket ne vegyük figyelembe
      if (other.id === kart.id || other.finished) continue;
      if (other.totalProgress > kart.totalProgress + 0.01) rank += 1;
      const hit = track.nearest(other.pos);
      const ahead = track.forward(info.s, hit.s);
      if (ahead <= track.totalLength * 0.5) {
        if (ahead < aheadKartDist) aheadKartDist = ahead;
        if (ahead < KART_AHEAD) {
          const need = kart.radius + other.radius + 8;
          pushAvoid(hit.lateral, ahead, need, 0.55, Math.hypot(other.pos.x - kart.pos.x, other.pos.y - kart.pos.y));
        }
      } else {
        const behind = track.totalLength - ahead;
        if (behind < behindKartDist) behindKartDist = behind;
      }
    }

    /* ---------------- ívvonal ---------------- */

    const insideDir = Math.sign(signedCurv);
    const insideAmount = cornerIntensity * linePrecision * halfW * 0.55;
    let lane = insideDir * insideAmount;
    lane += laneBias * halfW * 0.15;
    lane += Math.sin((view.time + wobblePhase) * wobbleFreq) * wobbleAmp * halfW * 0.5;
    if (kart.driftActive) lane += Math.sign(kart.driftDir) * halfW * 0.1;

    // item-doboz felé húzás, ha nincs tárgyunk
    if (kart.item === null) {
      let bestDist = BOX_AHEAD;
      let bestLat = 0;
      let found = false;
      for (const box of view.boxes) {
        if (!box.active) continue;
        const dx = box.pos.x - kart.pos.x;
        const dy = box.pos.y - kart.pos.y;
        if (dx * dx + dy * dy > BOX_AHEAD * BOX_AHEAD) continue;
        const hit = track.nearest(box.pos);
        const ahead = track.forward(info.s, hit.s);
        if (ahead >= bestDist) continue;
        bestDist = ahead;
        bestLat = hit.lateral;
        found = true;
      }
      if (found) {
        const pull = 0.55 * clamp(1 - bestDist / BOX_AHEAD, 0, 1);
        lane += (clamp(bestLat, -maxLane, maxLane) - lane) * pull;
      }
    }

    // ha már leszaladtunk az útról, előbb a középvonalra térjünk vissza
    const edge = clamp((Math.abs(info.lateral) - halfW * 0.9) / (halfW * 0.6), 0, 1);
    if (edge > 0) {
      const back = -Math.sign(info.lateral) * halfW * 0.35;
      lane = lane * (1 - edge) + back * edge;
    }
    lane = clamp(lane, -maxLane, maxLane);

    const laneLambda = kart.onTrack ? 5 : 12;
    laneSmooth += (lane - laneSmooth) * (1 - Math.exp(-laneLambda * dt));

    if (avoidLane !== null && avoidWeight > 0) {
      laneSmooth += (avoidLane - laneSmooth) * (1 - Math.exp(-18 * avoidWeight * dt));
    }
    laneSmooth = clamp(laneSmooth, -maxLane, maxLane);

    const aim = track.posAt(track.progressOfS(info.s + lookahead), laneSmooth);
    const aimX = aim.x - kart.pos.x;
    const aimY = aim.y - kart.pos.y;
    const desiredHeading = Math.hypot(aimX, aimY) > 1 ? Math.atan2(aimY, aimX) : kart.heading;
    const headingErr = angleDiff(desiredHeading, kart.heading);
    const desiredSteer = clamp(headingErr * STEER_GAIN * (kart.driftActive ? 1.06 : 1), -1, 1);

    // reakcióidő: a kormányszándékot késleltetve adjuk ki (determinisztikus gyűrű)
    steerHistory.push({ t: clock, v: desiredSteer });
    const keepFrom = clock - reaction - 0.4;
    while (steerHistory.length > 2 && steerHistory[0].t < keepFrom) steerHistory.shift();
    let steer = steerHistory[0].v;
    for (let i = steerHistory.length - 1; i >= 0; i--) {
      if (steerHistory[i].t <= clock - reaction) {
        steer = steerHistory[i].v;
        break;
      }
    }
    out.steer = clamp(steer, -1, 1);

    /* ---------------- drift ---------------- */

    if (driftLock > 0) driftLock = Math.max(0, driftLock - dt);
    let wantDrift = driftHold;
    if (!kart.driftActive) {
      const canStart =
        driftLock <= 0 &&
        Math.abs(signedCurv) > driftCurv &&
        forwardSpeed > maxSpeed * 0.52 &&
        Math.abs(out.steer) > 0.3 &&
        Math.sign(out.steer) === Math.sign(signedCurv);
      if (canStart) {
        driftHold = true;
        driftHoldTime = 0;
        wantDrift = true;
      } else if (driftHold) {
        driftHoldTime += dt;
        if (driftHoldTime > 0.5) {
          driftHold = false;
          wantDrift = false;
          driftLock = 0.4;
        }
      }
    } else {
      // kanyar végén elengedjük: mini-turbó
      const ending =
        Math.abs(signedCurv) < driftEndCurv ||
        kart.driftCharge > 2.2 ||
        forwardSpeed < maxSpeed * 0.38;
      if (ending) {
        driftHold = false;
        driftLock = 0.3;
        wantDrift = false;
        driftHoldTime = 0;
      } else {
        driftHold = true;
        wantDrift = true;
      }
    }
    out.drift = wantDrift;

    /* ---------------- gáz / fék ---------------- */

    const turnRate = kart.stats.turnRate;
    let targetSpeed = maxSpeed;
    for (let d = 20; d <= CORNER_LOOK; d += 30) {
      const k = Math.abs(track.curvatureAt(track.progressOfS(info.s + d)));
      if (k < 0.0006) continue;
      // elméleti ívsebesség: v * k <= turnRate * (1 - 0.48 * v / top)
      const cap = turnRate / (k + (turnRate * 0.48) / maxSpeed);
      const limit = Math.min(cap * safety, maxSpeed);
      // minél közelebb a kanyar, annál inkább a határsebesség számít
      const proximity = clamp((CORNER_LOOK - d) / 160, 0, 1);
      const allowNow = limit + (maxSpeed - limit) * (1 - proximity) * 0.85;
      if (allowNow < targetSpeed) targetSpeed = allowNow;
    }
    targetSpeed *= clamp(1 - Math.abs(headingErr) * 0.4, 0.62, 1);
    if (kart.driftActive) targetSpeed *= 0.95;

    const ratio = forwardSpeed / maxSpeed;
    const targetRatio = clamp(targetSpeed / maxSpeed, 0.2, 1);
    const over = ratio - targetRatio;
    if (over > 0.1) {
      out.throttle = 0;
      out.brake = clamp((over - 0.08) * 3.5, 0.25, 1);
    } else if (over > 0.012) {
      out.throttle = clamp(0.6 - over * 3, 0.3, 0.6);
      out.brake = 0;
    } else {
      out.throttle = 1;
      out.brake = 0;
    }
    if (kart.speed < -5) {
      // véletlenül hátrafelé gurulunk: inkább előre
      out.brake = 0;
      out.throttle = 1;
    }
    if (hardBrake > 0) {
      out.brake = Math.max(out.brake, hardBrake);
      out.throttle = Math.min(out.throttle, 0.15);
    }

    /* ---------------- item ---------------- */

    if (kart.item === null) {
      itemKnown = null;
      itemFired = false;
      itemWait = 0;
      itemHold = 0;
    } else if (kart.item !== itemKnown) {
      itemKnown = kart.item;
      itemFired = false;
      itemWait = clamp(itemDelay * (0.85 + rngItem.next() * 0.35), 0.2, 1.2);
      itemHold = 0;
    }

    if (kart.item !== null && kart.itemRollTimer <= 0 && !itemFired) {
      itemWait -= dt;
      itemHold += dt;
      const lineClear = straightness > 0.5;
      let wanted = false;
      switch (kart.item) {
        case 'turbo':
          wanted = lineClear || forwardSpeed < maxSpeed * 0.72 || itemHold > 1.5;
          break;
        case 'shield':
          wanted = true;
          break;
        case 'lightning':
          wanted = rank > 1; // csak ha van előttünk valaki
          break;
        case 'soda':
          wanted = lineClear && (aheadKartDist < 520 || itemHold > 1.2);
          break;
        case 'oil':
        case 'glue':
          wanted = behindKartDist < 320 || (lineClear && itemHold > 2.5);
          break;
        default:
          wanted = lineClear;
      }
      if (itemWait <= 0 && (wanted || itemHold > 7)) {
        out.useItem = true;
        itemFired = true;
      }
    }

    return out;
  };

  return { update };
}
